import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { useIdleLogout } from "../hooks/useIdleLogout";
import { LogOut, ShieldCheck, Clock, Lock, Loader2, Settings2 } from "lucide-react";

const WARN_SECONDS = 60;
const minKey = (uid) => `staff_idle_minutes_${uid || "anon"}`;
const actKey = (uid) => `staff_idle_action_${uid || "anon"}`;

export const StaffChrome = ({ title, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = useCallback(async () => { await logout(); navigate("/staff", { replace: true }); }, [logout, navigate]);

  const [minutes, setMinutes] = useState(() => {
    try { return localStorage.getItem(minKey(user?.user_id)) || "0"; } catch { return "0"; }
  });
  const [action, setAction] = useState(() => {
    try { return localStorage.getItem(actKey(user?.user_id)) || "lock"; } catch { return "lock"; }
  });
  const changeMinutes = (v) => {
    setMinutes(v);
    try { localStorage.setItem(minKey(user?.user_id), v); } catch { /* ignore */ }
    toast.success(v === "0" ? "You'll stay logged in on this device" : `Inactivity ${action === "lock" ? "lock" : "logout"} set to ${v} min`);
  };
  const changeAction = (v) => {
    setAction(v);
    try { localStorage.setItem(actKey(user?.user_id), v); } catch { /* ignore */ }
  };

  const [warnOpen, setWarnOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARN_SECONDS);
  const tick = useRef(null);
  const [locked, setLocked] = useState(false);

  const onWarn = useCallback((show) => {
    setWarnOpen(show);
    if (show) {
      setCountdown(WARN_SECONDS);
      if (tick.current) clearInterval(tick.current);
      tick.current = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    } else if (tick.current) {
      clearInterval(tick.current);
    }
  }, []);
  const onIdle = useCallback(async () => {
    if (tick.current) clearInterval(tick.current);
    setWarnOpen(false);
    if (action === "logout") {
      toast.info("Logged out after inactivity");
      await doLogout();
    } else {
      setLocked(true);
    }
  }, [action, doLogout]);
  const stayActive = () => { window.dispatchEvent(new Event("idle-stay-active")); setWarnOpen(false); };

  useIdleLogout(minutes !== "0" && !locked, parseInt(minutes, 10) || 15, WARN_SECONDS, onWarn, onIdle);
  useEffect(() => () => { if (tick.current) clearInterval(tick.current); }, []);

  const unlock = useCallback(() => { setLocked(false); window.dispatchEvent(new Event("idle-stay-active")); }, []);

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="bg-[#12234A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <ShieldCheck size={22} className="text-[#F5551E]" />
          <span className="font-display font-extrabold text-lg">{title}</span>
          <Badge className={`ml-2 ${user?.role === "admin" ? "bg-[#F5551E]" : "bg-emerald-600"} text-white capitalize`} data-testid="staff-role-badge">{user?.role}</Badge>
          <div className="ml-auto flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="staff-session-btn" className="text-white hover:bg-white/10 gap-1.5">
                  {minutes === "0" ? <ShieldCheck size={15} /> : <Clock size={15} />} Session
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72" data-testid="staff-session-popover">
                <p className="font-semibold text-sm text-[#12234A] mb-1 flex items-center gap-1.5"><Settings2 size={15} /> Inactivity</p>
                <p className="text-xs text-slate-500 mb-3">Stay logged in, or auto-lock/log out when you step away.</p>
                <Label className="text-xs font-semibold text-slate-600">After inactivity</Label>
                <Select value={minutes} onValueChange={changeMinutes}>
                  <SelectTrigger data-testid="staff-idle-minutes" className="h-9 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0" data-testid="idle-min-0">Stay logged in</SelectItem>
                    <SelectItem value="5" data-testid="idle-min-5">5 minutes</SelectItem>
                    <SelectItem value="15" data-testid="idle-min-15">15 minutes</SelectItem>
                    <SelectItem value="30" data-testid="idle-min-30">30 minutes</SelectItem>
                  </SelectContent>
                </Select>
                {minutes !== "0" && (
                  <>
                    <Label className="text-xs font-semibold text-slate-600 mt-3 block">Then</Label>
                    <Select value={action} onValueChange={changeAction}>
                      <SelectTrigger data-testid="staff-idle-action" className="h-9 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lock" data-testid="idle-act-lock">Lock — ask for password</SelectItem>
                        <SelectItem value="logout" data-testid="idle-act-logout">Log me out</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </PopoverContent>
            </Popover>
            <span className="text-sm text-slate-300 hidden sm:inline">{user?.name}</span>
            <Button onClick={doLogout} variant="ghost" size="sm" data-testid="staff-logout-btn" className="text-white hover:bg-white/10 gap-1.5"><LogOut size={15} /> Logout</Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">{children}</main>

      <Dialog open={warnOpen && !locked} onOpenChange={(o) => { if (!o) stayActive(); }}>
        <DialogContent className="max-w-sm" data-testid="idle-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock size={18} className="text-[#F5551E]" /> Still there?</DialogTitle>
            <DialogDescription>
              You'll be {action === "lock" ? "locked" : "logged out"} in <span data-testid="idle-warning-countdown" className="font-bold text-[#12234A]">{countdown}s</span> due to inactivity.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onIdle} data-testid="idle-warning-now" className="rounded-xl">{action === "lock" ? "Lock now" : "Log out now"}</Button>
            <Button onClick={stayActive} data-testid="idle-warning-stay" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">Stay active</Button>
          </div>
        </DialogContent>
      </Dialog>

      {locked && <LockScreen user={user} onUnlock={unlock} onLogout={doLogout} />}
    </div>
  );
};

const LockScreen = ({ user, onUnlock, onLogout }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-password", { password });
      toast.success("Welcome back!");
      setPassword("");
      onUnlock();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-[#12234A]/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lock-screen">
      <form onSubmit={submit} className="bg-white rounded-2xl p-7 w-full max-w-sm shadow-2xl">
        <div className="flex items-center gap-2 mb-1"><Lock size={20} className="text-[#F5551E]" /><h2 className="font-display text-xl font-extrabold text-[#12234A]">Session locked</h2></div>
        <p className="text-sm text-slate-500 mb-4">Signed in as <strong>{user?.name}</strong>. Enter your password to resume.</p>
        <Label className="text-xs font-semibold text-slate-600">Password</Label>
        <Input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} data-testid="lock-password" className="rounded-xl mt-1" placeholder="Your password" />
        <Button type="submit" disabled={busy} data-testid="lock-unlock-btn" className="w-full rounded-xl mt-4 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Unlock</Button>
        <button type="button" onClick={onLogout} data-testid="lock-logout-btn" className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-3">Log out instead</button>
      </form>
    </div>
  );
};
